import React, { useState, useEffect, useMemo } from 'react';
import { Search, Filter, Eye, Edit, Package, TrendingUp, DollarSign, BarChart3, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import apinode from '../../services/apinode';

/** picture_id -> URL ML */
const getPictureUrlFromId = (pictureId) => {
  if (!pictureId) return placeholderSVG;
  return `https://http2.mlstatic.com/D_NQ_NP_${pictureId}-O.webp`;
};

/** Normaliza health (0–1 ou 0–100) para 0–100 */
const normalizeHealth = (h) => {
  if (h == null || isNaN(h)) return 0;
  const v = h <= 1 ? h * 100 : h;
  return Math.max(0, Math.min(100, v));
};

/** Gráfico circular de saúde (SVG) */
const CircularHealth = ({ value = 0, size = 48, stroke = 6 }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (value / 100) * c;
  const rem = c - dash;

  let color = '#dc3545'; // red
  if (value >= 66) color = '#198754'; // green
  else if (value >= 50) color = '#ffc107'; // yellow

  return (
    <div className="d-flex align-items-center" title={`Saúde: ${Math.round(value)}%`}>
      <svg width={size} height={size} style={{ display: 'block' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="#e9ecef"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={color}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={`${dash} ${rem}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text
          x="50%"
          y="50%"
          dominantBaseline="central"
          textAnchor="middle"
          fontSize="12"
          fontWeight="700"
          fill="#343a40"
        >
          {Math.round(value)}%
        </text>
      </svg>
    </div>
  );
};

/**
 * Props:
 * - conta: string (id da conta)
 * - sellerId: string|null
 * - contasOptions: array opcional de { value: 'idconta', label: 'Minha Conta' }
 */
const AnunciosListing = ({ conta = 'default', sellerId = null, contasOptions = [] }) => {
  const [anuncios, setAnuncios] = useState([]);
  const [filteredAnuncios, setFilteredAnuncios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [error, setError] = useState(null);
  const [selectedConta, setSelectedConta] = useState(conta || 'default');
  const [previewImg, setPreviewImg] = useState(null);

  const [variacoesByMlb, setVariacoesByMlb] = useState({});
  const [loadingVariacoes, setLoadingVariacoes] = useState({});
  const [erroVariacoes, setErroVariacoes] = useState({});

  const itemsPerPage = 10;

  useEffect(() => {
    if (conta && conta !== selectedConta) setSelectedConta(conta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conta]);

  const buildListUrl = (_contaId = selectedConta, _sellerId = sellerId) => {
    const base = `/meli/api/listar/${_contaId}`;
    return _sellerId ? `${base}?seller_id=${_sellerId}` : base;
  };

  const placeholderSVG =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120">
        <rect width="100%" height="100%" fill="#f1f3f5"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="12" fill="#6c757d">Sem imagem</text>
      </svg>`
    );

  const normalizeUrl = (u) => (typeof u === 'string' && u.trim().length ? u : null);

  const getAnuncioImage = (anuncio) =>
    normalizeUrl(anuncio?.thumbnail) ||
    normalizeUrl(anuncio?.picture) ||
    normalizeUrl(anuncio?.imagem) ||
    normalizeUrl(anuncio?.images?.[0]) ||
    normalizeUrl(anuncio?.variacoes?.find(v => v?.image)?.image) ||
    placeholderSVG;

  const getVariacaoImage = (variacao) => normalizeUrl(variacao?.image) || placeholderSVG;

  const [derivedContas, setDerivedContas] = useState([]);

  // Carrega contas da API
  useEffect(() => {
    const fetchContas = async () => {
      try {
        const idConta = localStorage.getItem('idconta');
        const res = await apinode.get(`/meli/api/contas/${idConta}`);
        const data = res.data?.dados || res.data;

        const contas = data.map((conta) => ({
          value: conta.id_conta || conta.id || conta.codigo,
          label: conta.nome || conta.usuario || conta.apelido
        }));

        setDerivedContas(contas);

        if (contas.length > 0) {
          setSelectedConta(contas[0].value); // Aqui você está salvando o id_conta
        }
      } catch (error) {
        console.error('Erro ao buscar contas:', error);
      }
    };

    fetchContas();
  }, []);



  useEffect(() => {
    const fetchAnuncios = async () => {
      try {
        setLoading(true);
        setError(null);
        const url = buildListUrl();
        const response = await apinode.get(`/meli/api/listar/514066249?seller_id=${selectedConta}`); // ajuste o path conforme seu backend
        if (response.data?.sucesso) {
          setAnuncios(response.data.dados);
          setFilteredAnuncios(response.data.dados);
        } else {
          setError('Erro ao carregar dados da API');
        }
      } catch (error) {
        console.error('Erro ao buscar anúncios:', error);
        setError(error?.response?.data?.erro || 'Erro ao conectar com a API');

        // Fallback de exemplo
        const mockData = {
          sucesso: true,
          total: 1,
          dados: [
            {
              id: 1,
              codigomk: 'MLB123456789',
              title: 'Produto Demo',
              category_id: 'MLB1234',
              status: 'active',
              seller_id: '123456789',
              created_at: '2024-01-15',
              updated_at: '2024-08-06',
              health: 0.86, // 86%
              thumbnail: null,
              variacoes: [
                {
                  idvariacao: 101,
                  skuvariacao: 'SKU-101',
                  image: null,
                  gtin: '7890000000000',
                  price: 199.9,
                  available_quantity: 7,
                  sold_quantity: 15
                }
              ]
            }
          ]
        };
        setAnuncios(mockData.dados);
        setFilteredAnuncios(mockData.dados);
      } finally {
        setLoading(false);
      }
    };
    fetchAnuncios();
  }, [selectedConta, sellerId]);

  useEffect(() => {
    let filtered = anuncios;

    if (searchTerm) {
      const needle = searchTerm.toLowerCase();
      filtered = filtered.filter((anuncio) =>
        (anuncio.title || '').toLowerCase().includes(needle) ||
        (anuncio.codigomk || '').toLowerCase().includes(needle) ||
        anuncio.variacoes?.some((v) => (v.skuvariacao || '').toLowerCase().includes(needle))
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((anuncio) => anuncio.status === statusFilter);
    }
    setFilteredAnuncios(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, anuncios]);

  const getStatusBadge = (status) => {
    const badges = {
      active: { class: 'bg-success', text: 'Ativo' },
      paused: { class: 'bg-warning', text: 'Pausado' },
      inactive: { class: 'bg-secondary', text: 'Inativo' }
    };
    return badges[status] || badges.inactive;
  };

  const calculateTotalStock = (variacoes = []) =>
    variacoes.reduce((sum, v) => sum + (v.available_quantity || 0), 0);

  const calculateTotalSold = (variacoes = []) =>
    variacoes.reduce((sum, v) => sum + (v.sold_quantity || 0), 0);

  const getMinPrice = (variacoes = []) => Math.min(...variacoes.map((v) => v.price));
  const getMaxPrice = (variacoes = []) => Math.max(...variacoes.map((v) => v.price));

  const formatPrice = (price) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price || 0);

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('pt-BR');

  const loadVariacoesFromML = async (mlb) => {
    setLoadingVariacoes((s) => ({ ...s, [mlb]: true }));
    setErroVariacoes((s) => ({ ...s, [mlb]: null }));
    try {
      const url = `/meli/api/anuncios/${localStorage.idconta || selectedConta}/${mlb}/variacoes-ml`;
      const resp = await apinode.get(url);
      if (resp.data?.sucesso) {
        setVariacoesByMlb((s) => ({ ...s, [mlb]: resp.data.variacoes || [] }));
      } else {
        throw new Error(resp.data?.erro || 'Erro desconhecido ao carregar variações');
      }
    } catch (e) {
      setErroVariacoes((s) => ({ ...s, [mlb]: e.message || 'Falha ao buscar variações' }));
    } finally {
      setLoadingVariacoes((s) => ({ ...s, [mlb]: false }));
    }
  };

  const toggleRow = (anuncio) => {
    const next = new Set(expandedRows);
    if (next.has(anuncio.id)) {
      next.delete(anuncio.id);
    } else {
      next.add(anuncio.id);
      const mlb = anuncio.codigomk;
      if (!variacoesByMlb[mlb] && !loadingVariacoes[mlb]) {
        loadVariacoesFromML(mlb);
      }
    }
    setExpandedRows(next);
  };

  const refreshData = async () => {
    try {
      const response = await apinode.get(buildListUrl());
      if (response.data?.sucesso) {
        setAnuncios(response.data.dados);
        setFilteredAnuncios(response.data.dados);
      }
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
    }
  };

  const totalPages = Math.ceil(filteredAnuncios.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAnuncios.slice(indexOfFirstItem, indexOfLastItem);

  const goToPage = (p) => {
    const page = Math.min(Math.max(1, p), totalPages);
    setCurrentPage(page);
  };

  const getPaginationRange = () => {
    const deltaSide = 2;
    const range = [];
    const left = Math.max(2, currentPage - deltaSide);
    const right = Math.min(totalPages - 1, currentPage + deltaSide);
    range.push(1);
    if (left > 2) range.push('ellipsis-left');
    for (let i = left; i <= right; i++) range.push(i);
    if (right < totalPages - 1) range.push('ellipsis-right');
    if (totalPages > 1) range.push(totalPages);
    return range;
  };

  const inferColor = (variacao, anuncio) => {
    const text = `${variacao?.skuvariacao || ''} ${anuncio?.title || ''}`.toLowerCase();
    const patterns = [
      { label: 'Preto', test: /(preto|black|blk|pto|negro)/ },
      { label: 'Branco', test: /(branco|white|wht|brc)/ },
      { label: 'Azul', test: /(azul|blue|blu)/ },
      { label: 'Vermelho', test: /(vermelho|red|rd)/ },
      { label: 'Verde', test: /(verde|green|grn)/ },
      { label: 'Cinza', test: /(cinza|gray|grey|gry)/ },
      { label: 'Rosa', test: /(rosa|pink|pnk)/ },
    ];
    for (const p of patterns) if (p.test.test(text)) return p.label;
    return '—';
  };

  const colorBadgeClass = (color) => {
    switch ((color || '').toLowerCase()) {
      case 'preto': return 'badge bg-dark text-white';
      case 'branco': return 'badge bg-light text-dark border';
      case 'azul': return 'badge bg-primary';
      case 'vermelho': return 'badge bg-danger';
      case 'verde': return 'badge bg-success';
      case 'cinza': return 'badge bg-secondary';
      case 'rosa': return 'badge bg-danger';
      default: return 'badge bg-secondary';
    }
  };

  const formatCombos = (variacao) => {
    const combos = variacao?.attribute_combinations || [];
    if (!combos.length) return '—';
    return combos.map(c => `${c.name || c.id}: ${c.value_name ?? (c.values?.[0]?.name || '—')}`).join(' | ');
  };

  const formatAttrs = (variacao) => {
    const attrs = variacao?.attributes || [];
    if (!attrs.length) return '—';
    return attrs.map(a => `${a.name || a.id}: ${a.value_name ?? (a.values?.[0]?.name || '—')}`).join(' | ');
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <h5 className="text-muted">Carregando anúncios...</h5>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="alert alert-danger text-center">
              <h5>Erro ao carregar anúncios</h5>
              <p>{error}</p>
              <button className="btn btn-primary" onClick={() => window.location.reload()}>
                Tentar novamente
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h2 className="fw-bold mb-1 text-dark">Meus Anúncios</h2>
              <p className="text-muted mb-0">Gerencie todos os seus produtos no Mercado Livre</p>
            </div>

            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0 me-2 text-muted">Conta:</label>
              <select
                className="form-select"
                value={selectedConta}
                onChange={(e) => setSelectedConta(e.target.value)}
                style={{ minWidth: 220 }}
              >
                {derivedContas.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

            </div>

            <button className="btn btn-primary px-4">
              <Package className="me-2" size={16} />
              Novo Anúncio
            </button>
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className="row mb-4">
        <div className="col-md-3 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="bg-primary bg-opacity-10 rounded-3 p-3 me-3">
                  <Package className="text-primary" size={24} />
                </div>
                <div>
                  <h3 className="mb-0 fw-bold">{anuncios.length}</h3>
                  <small className="text-muted">Total de Anúncios</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="bg-success bg-opacity-10 rounded-3 p-3 me-3">
                  <TrendingUp className="text-success" size={24} />
                </div>
                <div>
                  <h3 className="mb-0 fw-bold">{anuncios.filter((a) => a.status === 'active').length}</h3>
                  <small className="text-muted">Anúncios Ativos</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="bg-info bg-opacity-10 rounded-3 p-3 me-3">
                  <BarChart3 className="text-info" size={24} />
                </div>
                <div>
                  <h3 className="mb-0 fw-bold">
                    {anuncios.reduce((sum, a) => sum + calculateTotalStock(a.variacoes), 0)}
                  </h3>
                  <small className="text-muted">Estoque Total</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="bg-warning bg-opacity-10 rounded-3 p-3 me-3">
                  <DollarSign className="text-warning" size={24} />
                </div>
                <div>
                  <h3 className="mb-0 fw-bold">
                    {anuncios.reduce((sum, a) => sum + calculateTotalSold(a.variacoes), 0)}
                  </h3>
                  <small className="text-muted">Vendas Totais</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <Search size={16} className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Buscar por título, código MLB ou SKU..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Todos os status</option>
                <option value="active">Ativo</option>
                <option value="paused">Pausado</option>
                <option value="inactive">Inativo</option>
              </select>
            </div>
            <div className="col-md-3">
              <button className="btn btn-outline-primary w-100">
                <Filter size={16} className="me-2" />
                Filtros Avançados
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted">
          Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredAnuncios.length)} de {filteredAnuncios.length} anúncios
        </span>
        <button className="btn btn-outline-secondary btn-sm" onClick={refreshData}>
          <RefreshCw size={14} className="me-1" />
          Atualizar
        </button>
      </div>

      {/* Tabela */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="border-0 fw-semibold py-3">Imagem</th>
                  <th className="border-0 fw-semibold py-3">Produto</th>
                  <th className="border-0 fw-semibold py-3">Status</th>
                  <th className="border-0 fw-semibold py-3">Saúde</th>{/* NOVO */}
                  <th className="border-0 fw-semibold py-3">Preço</th>
                  <th className="border-0 fw-semibold py-3">Estoque</th>
                  <th className="border-0 fw-semibold py-3">Vendidos</th>
                  <th className="border-0 fw-semibold py-3">Variações</th>
                  <th className="border-0 fw-semibold py-3">Atualizado</th>
                  <th className="border-0 fw-semibold py-3 text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((anuncio) => {
                  const statusBadge = getStatusBadge(anuncio.status);
                  const totalStock = calculateTotalStock(anuncio.variacoes || []);
                  const totalSold = calculateTotalSold(anuncio.variacoes || []);
                  const minPrice = getMinPrice(anuncio.variacoes || [{ price: 0 }]);
                  const maxPrice = getMaxPrice(anuncio.variacoes || [{ price: 0 }]);
                  const imgUrl = getAnuncioImage(anuncio);

                  const healthPct = normalizeHealth(anuncio.health); // NOVO

                  const mlb = anuncio.codigomk;
                  const isRowOpen = expandedRows.has(anuncio.id);
                  const isLoadingVar = !!loadingVariacoes[mlb];
                  const loadErrorVar = erroVariacoes[mlb];
                  const variacoesFonte = variacoesByMlb[mlb] || anuncio.variacoes || [];

                  return (
                    <React.Fragment key={anuncio.id}>
                      <tr className="align-middle">
                        <td className="py-3" style={{ width: 92 }}>
                          <button
                            className="btn p-0 border-0 bg-transparent"
                            onClick={() => setPreviewImg(imgUrl)}
                            title="Ampliar imagem"
                          >
                            <img
                              src={imgUrl}
                              alt={anuncio.title}
                              loading="lazy"
                              onError={(e) => (e.currentTarget.src = placeholderSVG)}
                              style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, boxShadow: '0 0 0 1px rgba(0,0,0,0.05)' }}
                            />
                          </button>
                        </td>
                        <td className="py-3">
                          <div className="d-flex align-items-center">
                            <button
                              className="btn btn-sm btn-outline-secondary me-2 p-1"
                              onClick={() => toggleRow(anuncio)}
                              style={{ width: '28px', height: '28px' }}
                            >
                              {isRowOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                            <div>
                              <h6 className="mb-1 fw-semibold text-truncate" style={{ maxWidth: '280px' }}>
                                {anuncio.title}
                              </h6>
                              <small className="text-muted">
                                <strong>MLB:</strong> {anuncio.codigomk} | <strong>Categoria:</strong> {anuncio.category_id} - {anuncio.category_name}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td className="py-3">
                          <span className={`badge ${statusBadge.class} px-2 py-1`}>{statusBadge.text}</span>
                        </td>

                        {/* NOVO: Saúde */}
                        <td className="py-3">
                          <CircularHealth value={healthPct} />
                        </td>

                        <td className="py-3">
                          <div className="fw-semibold">
                            {minPrice === maxPrice ? formatPrice(minPrice) : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`}
                          </div>
                        </td>
                        <td className="py-3">
                          <span className={`fw-semibold ${totalStock < 5 ? 'text-danger' : totalStock < 10 ? 'text-warning' : 'text-success'}`}>
                            {totalStock}
                          </span>
                          <small className="text-muted d-block">unidades</small>
                        </td>
                        <td className="py-3">
                          <span className="fw-semibold text-primary">{totalSold}</span>
                          <small className="text-muted d-block">vendidas</small>
                        </td>
                        <td className="py-3">
                          <span className="badge bg-secondary">{(anuncio.variacoes?.length || 0)} variação(ões)</span>
                        </td>
                        <td className="py-3">
                          <small className="text-muted">{formatDate(anuncio.last_updated)}</small>
                        </td>
                        <td className="py-3 text-center">
                          <div className="btn-group" role="group">
                            <button className="btn btn-outline-primary btn-sm" title="Visualizar">
                              <Eye size={14} />
                            </button>
                            <button className="btn btn-outline-secondary btn-sm" title="Editar">
                              <Edit size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Linha expandida */}
                      {isRowOpen && (
                        <tr className="bg-light">
                          <td colSpan={10} className="p-0">
                            <div className="p-3 border-top">
                              <h6 className="mb-3 text-primary">Variações do Produto</h6>

                              {isLoadingVar && (
                                <div className="d-flex align-items-center text-muted mb-2">
                                  <div className="spinner-border spinner-border-sm me-2" role="status" />
                                  Carregando variações...
                                </div>
                              )}
                              {!!loadErrorVar && !isLoadingVar && (
                                <div className="alert alert-warning py-2">
                                  {loadErrorVar}
                                  <button className="btn btn-sm btn-link ms-2" onClick={() => loadVariacoesFromML(mlb)}>
                                    Tentar novamente
                                  </button>
                                </div>
                              )}

                              <div className="table-responsive">
                                <table className="table table-sm mb-0">
                                  <thead>
                                    <tr className="table-secondary">
                                      <th className="fw-semibold">Imagem</th>
                                      <th className="fw-semibold">ID</th>
                                      <th className="fw-semibold">SKU</th>
                                      <th className="fw-semibold">Combinações</th>
                                      <th className="fw-semibold">Atributos</th>
                                      <th className="fw-semibold">GTIN/EAN</th>
                                      <th className="fw-semibold">Preço</th>
                                      <th className="fw-semibold">Disponível</th>
                                      <th className="fw-semibold">Vendidos</th>
                                      <th className="fw-semibold">Status Estoque</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(variacoesFonte).map((variacao) => {
                                      const idvariacao = variacao.idvariacao ?? variacao.id;
                                      const price = variacao.price ?? 0;
                                      const available = variacao.available_quantity ?? 0;
                                      const sold = variacao.sold_quantity ?? 0;

                                      const dbVar = (anuncio.variacoes || []).find(v => String(v.idvariacao) === String(idvariacao));

                                      const combosStr = formatCombos(variacao);
                                      const attrsStr = formatAttrs(variacao);

                                      const gtin = dbVar?.gtin ?? variacao.gtin ?? null;
                                      const sku = dbVar?.skuvariacao ?? variacao.skuvariacao ?? '—';

                                      let vImg = dbVar?.image || placeholderSVG;
                                      if (!dbVar?.image && Array.isArray(variacao.picture_ids) && variacao.picture_ids.length > 0) {
                                        vImg = getPictureUrlFromId(variacao.picture_ids[0]);
                                      }

                                      const colorFromCombo = (variacao.attribute_combinations || []).find(
                                        ac => (ac.id === 'COLOR' || /cor/i.test(ac.name || ''))
                                      );
                                      const cor = colorFromCombo?.value_name || inferColor(dbVar || variacao, anuncio);

                                      return (
                                        <tr key={idvariacao}>
                                          <td style={{ width: 76 }}>
                                            <button
                                              className="btn p-0 border-0 bg-transparent"
                                              onClick={() => setPreviewImg(vImg)}
                                              title="Ampliar imagem"
                                            >
                                              <img
                                                src={vImg}
                                                alt={sku || idvariacao}
                                                loading="lazy"
                                                onError={(e) => (e.currentTarget.src = placeholderSVG)}
                                                style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, boxShadow: '0 0 0 1px rgba(0,0,0,0.05)' }}
                                              />
                                            </button>
                                          </td>

                                          <td><span className="text-muted">{idvariacao}</span></td>
                                          <td><span className="fw-semibold">{sku}</span></td>
                                          <td><small className="text-muted">{combosStr}</small></td>
                                          <td><small className="text-muted">{attrsStr}</small></td>
                                          <td><span className="text-muted font-monospace">{gtin || 'N/A'}</span></td>
                                          <td><span className="fw-semibold text-success">{formatPrice(price)}</span></td>
                                          <td>
                                            <span className={`fw-semibold ${available === 0
                                              ? 'text-danger'
                                              : available < 5
                                                ? 'text-danger'
                                                : available < 10
                                                  ? 'text-warning'
                                                  : 'text-success'
                                              }`}>
                                              {available}
                                            </span>
                                          </td>
                                          <td><span className="text-primary fw-semibold">{sold}</span></td>
                                          <td>
                                            {available === 0 ? (
                                              <span className="badge bg-danger">Sem Estoque</span>
                                            ) : available < 5 ? (
                                              <span className="badge bg-warning">Estoque Baixo</span>
                                            ) : (
                                              <span className="badge bg-success">Disponível</span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => goToPage(1)} disabled={currentPage === 1}>
                  « Primeiro
                </button>
              </li>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                  ‹ Anterior
                </button>
              </li>
              {getPaginationRange().map((p, idx) =>
                typeof p === 'string' ? (
                  <li key={`${p}-${idx}`} className="page-item disabled">
                    <span className="page-link">…</span>
                  </li>
                ) : (
                  <li key={p} className={`page-item ${currentPage === p ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(p)}>{p}</button>
                  </li>
                )
              )}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                  Próximo ›
                </button>
              </li>
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages}>
                  Último »
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Modal de preview */}
      {previewImg && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ background: 'rgba(0,0,0,0.6)', zIndex: 1050 }}
          onClick={() => setPreviewImg(null)}
        >
          <div className="d-flex justify-content-center align-items-center w-100 h-100 p-3">
            <img
              src={previewImg}
              alt="Preview"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '95vw', maxHeight: '90vh', borderRadius: 8, background: '#fff' }}
              onError={(e) => (e.currentTarget.src = placeholderSVG)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AnunciosListing;
